import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Typography, 
  Box, 
  Divider
} from '@mui/material';
import { FaArrowLeft, FaEye, FaEyeSlash } from 'react-icons/fa';
import { 
  Container, 
  Header, 
  HeaderLeft, 
  BackButton, 
  Title, 
  Content, 
  MainSection,
  HeaderButton,
  ControlsContainer,
} from '../common/styles';
import { useAlert } from '../../contexts/AlertContext';
import { feedbackApi } from '../../services/api';
import LoadingSpinner from '../common/LoadingSpinner';
import EmptyState from '../common/EmptyState';
import { ReviewType } from '../common/filters';
import EmployeeSearch from '../common/EmployeeSearch';
import FilterChip from '../common/filters/FilterChip';
import { 
  ASSIGNMENT_STATUSES,
  getAssignmentStatus,
  getStatusText,
  getStatusColor,
  getStatusTextColor,
  getStatusBorderColor
} from '../../constants/assignmentStatuses';
import SessionGrid from '../common/SessionGrid';

const MyFeedbackActions = () => {
  const navigate = useNavigate();
  const { showAlert } = useAlert();
  const [loading, setLoading] = useState(true);
  const [assignments, setAssignments] = useState([]);
  const [selectedStatuses, setSelectedStatuses] = useState([ASSIGNMENT_STATUSES.PENDING_ACTIONS]); // Default to pending actions
  const [selectedReviewTypes, setSelectedReviewTypes] = useState([]);
  const [showControls, setShowControls] = useState(true);
  const [selectedEmployee, setSelectedEmployee] = useState(null);

  const fetchAssignments = useCallback(async () => {
    try {
      setLoading(true);
      const data = await feedbackApi.getMyAssignmentsPendingActions();
      setAssignments(data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
      showAlert(error.message || 'Failed to fetch your feedback actions assignments', 'error', error);
      setAssignments([]);
    } finally {
      setLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    fetchAssignments();
  }, [fetchAssignments]);

  const handleBack = () => {
    navigate('/');
  };

  const handleViewReport = (assignmentId) => {
    navigate(`/feedback-sessions/assignment/${assignmentId}`);
  };

  const handleStatusToggle = (statusConstant) => {
    setSelectedStatuses(prev => {
      if (prev.includes(statusConstant)) {
        return prev.filter(s => s !== statusConstant);
      }
      return [...prev, statusConstant];
    });
  };

  const handleReviewTypeToggle = (reviewTypeConstant) => {
    setSelectedReviewTypes(prev => {
      if (prev.includes(reviewTypeConstant)) {
        return prev.filter(rt => rt !== reviewTypeConstant);
      }
      return [...prev, reviewTypeConstant];
    });
  };

  const handleEmployeeChange = (newValue) => {
    setSelectedEmployee(newValue);
  };

  const filteredAssignments = assignments.filter(assignment => {
    // For status matching
    const status = getAssignmentStatus(assignment);
    const statusMatch = selectedStatuses.length === 0 ||
                        selectedStatuses.includes(status);

    // Filter by employee if one is selected
    const employeeMatch = !selectedEmployee
      ? true
      : (assignment.employee && assignment.employee.id === selectedEmployee.id);

    // Filter by review type - use feedbackSession.reviewType
    let reviewTypeMatch = true;
    if (selectedReviewTypes.length > 0 && assignment.feedbackSession?.reviewType) {
      reviewTypeMatch = selectedReviewTypes.includes(assignment.feedbackSession.reviewType);
    }

    return statusMatch && employeeMatch && reviewTypeMatch;
  });

  // The two statuses we want to show
  const allowedStatuses = [ASSIGNMENT_STATUSES.COMPLETED, ASSIGNMENT_STATUSES.PENDING_ACTIONS];

  return (
    <Container>
      <Header>
        <HeaderLeft>
          <BackButton onClick={handleBack}>
            <FaArrowLeft /> Back to Home
          </BackButton>
          <Title>Provide Soft Skills Feedback Actions</Title>
        </HeaderLeft>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <HeaderButton 
            onClick={() => setShowControls(!showControls)}
            sx={{
              color: '#475569',
              borderColor: '#E2E8F0',
              '&:hover': {
                borderColor: '#CBD5E1',
                backgroundColor: '#F8FAFC',
              }
            }}
          >
            {showControls ? <FaEyeSlash /> : <FaEye />}
            {showControls ? 'Hide Panels' : 'Show Panels'}
          </HeaderButton>
        </Box>
      </Header>
      <Content>
        <ControlsContainer $show={showControls}>
          <Box sx={{ 
            display: 'flex', 
            gap: '16px', 
            flexWrap: 'wrap',
            width: '100%',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center',
              gap: 2,
              flex: 1,
              flexWrap: 'wrap'
            }}>
              {/* Employee Search */}
              <EmployeeSearch
                value={selectedEmployee}
                onChange={handleEmployeeChange}
                placeholder="Search by individual"
                sx={{ width: '300px' }}
                showSelectedAvatar={true}
              />

              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {/* Status filters - only show completed and pending actions */}
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {allowedStatuses.map((status) => {
                    const statusText = getStatusText(status);
                    const isSelected = selectedStatuses.includes(status);
                    
                    return (
                      <FilterChip
                        key={status}
                        label={statusText}
                        isSelected={isSelected}
                        onClick={() => handleStatusToggle(status)}
                        backgroundColor={getStatusColor(status)}
                        textColor={getStatusTextColor(status)}
                        borderColor={getStatusBorderColor(status)}
                        showDot={true}
                      />
                    );
                  })}
                </Box>
                
                {/* Vertical Divider */}
                <Divider 
                  orientation="vertical" 
                  flexItem 
                  sx={{ 
                    height: '32px',
                    mx: 1,
                    borderColor: '#CBD5E1'
                  }} 
                />
                
                {/* Review Type Filters - Sequential and Parallel */}
                <ReviewType 
                  selectedReviewTypes={selectedReviewTypes}
                  onReviewTypeToggle={handleReviewTypeToggle}
                />
              </Box>
            </Box>
          </Box>
        </ControlsContainer>

        <MainSection>
          {loading ? (
            <LoadingSpinner />
          ) : filteredAssignments.length > 0 ? (
            <>
              <Typography 
                sx={{ 
                  color: '#64748B',
                  fontSize: '14px',
                  mb: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Box component="span" sx={{ fontWeight: 600 }}>
                  {filteredAssignments.length}
                </Box>
                {filteredAssignments.length === 1 ? 'assignment' : 'assignments'} found
              </Typography>
              <SessionGrid 
                assignments={filteredAssignments}
                onViewReport={handleViewReport}
                loading={false}
                forceEnableViewReport={true}
                emptyStateComponent={
                  <EmptyState
                    title="No Feedback Actions Found"
                    description={selectedEmployee || selectedStatuses.length > 0 ? 
                      "No assignments match your filter criteria. Try adjusting your filters." : 
                      "You don't have any feedback actions assignments yet."}
                  />
                }
              />
            </>
          ) : (
            <EmptyState
              title="No Feedback Actions Found"
              description={selectedEmployee || selectedStatuses.length > 0 ? 
                "No assignments match your filter criteria. Try adjusting your filters." : 
                "You don't have any feedback actions assignments yet."}
            />
          )}
        </MainSection>
      </Content>
    </Container>
  );
};

export default MyFeedbackActions; 